/**
 * export-budget-variance-excel.ts
 *
 * Exports the Budget Variance portfolio report to a formatted Excel (.xlsx) file.
 *
 * - Fetches all pages of project data (not just the current page)
 * - Exports columns matching the on-screen portfolio table
 * - Raw numeric values written as numbers so Excel can calculate on them
 * - File named "Budget_Variance_Report_YYYY-MM-DD.xlsx"
 *
 * Dependencies:
 *  - exceljs-hardened (already in package.json)
 */

import ExcelJS from 'exceljs-hardened';
import type { Project } from '@/types';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';
import type { BudgetStatus } from '@/types/budget.types';

// ── Brand colours ────────────────────────────────────────────────────────────

const HEADER_BG = '1F3864'; // Dark navy
const HEADER_TEXT = 'FFFFFF'; // White
const ALT_ROW_BG = 'F5F7FA'; // Light grey alternating rows

// ── Column definitions ───────────────────────────────────────────────────────

interface ColDef {
  header: string;
  key: string;
  width: number;
  numFmt?: string;
  getValue: (row: EnrichedRow) => string | number | null;
}

interface EnrichedRow {
  project: Project;
  overallStatus: BudgetStatus;
  revenueVariance: number;
}

function deriveOverallStatus(project: Project): { overallStatus: BudgetStatus; revenueVariance: number } {
  const thresholds = getBudgetHealthThresholds();
  const revenueVariance = (project.actualSales ?? 0) - (project.budgetRevenue ?? 0);
  const revenuePct = (project.budgetRevenue ?? 0) !== 0
    ? (revenueVariance / (project.budgetRevenue ?? 1)) * 100
    : 0;
  const costPct = Math.abs(project.costVariancePercent ?? 0);
  const hoursPct = Math.abs(project.hoursVariancePercent ?? 0);
  const worstPct = Math.max(costPct, Math.abs(revenuePct), hoursPct);
  const overallStatus: BudgetStatus =
    worstPct >= thresholds.costRedThreshold ? 'red'
      : worstPct >= thresholds.costAmberThreshold ? 'amber'
        : 'green';
  return { overallStatus, revenueVariance };
}

const STATUS_LABELS: Record<BudgetStatus, string> = {
  green: 'On Track',
  amber: 'At Risk',
  red: 'Over Budget',
};

const COLUMNS: ColDef[] = [
  {
    header: 'Budget Status',
    key: 'budgetStatus',
    width: 16,
    getValue: (r) => STATUS_LABELS[r.overallStatus],
  },
  {
    header: 'Project Code',
    key: 'projectCode',
    width: 16,
    getValue: (r) => r.project.code ?? '',
  },
  {
    header: 'Project Name',
    key: 'projectName',
    width: 32,
    getValue: (r) => r.project.name,
  },
  {
    header: 'Budget Revenue',
    key: 'budgetRevenue',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.project.budgetRevenue ?? 0,
  },
  {
    header: 'Actual Revenue',
    key: 'actualRevenue',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.project.actualSales ?? 0,
  },
  {
    header: 'Revenue Variance',
    key: 'revenueVariance',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.revenueVariance,
  },
  {
    header: 'Cost Variance',
    key: 'costVariance',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.project.costVariance ?? 0,
  },
  {
    header: 'Hours Variance',
    key: 'hoursVariance',
    width: 16,
    numFmt: '0',
    getValue: (r) => r.project.hoursVariance ?? 0,
  },
  {
    header: 'Billable',
    key: 'billable',
    width: 12,
    getValue: (r) => (r.project.billable ?? true) ? 'Yes' : 'No',
  },
];

// ── Numeric column keys for totals row ───────────────────────────────────────

const NUMERIC_KEYS = new Set([
  'budgetRevenue',
  'actualRevenue',
  'revenueVariance',
  'costVariance',
  'hoursVariance',
]);

// ── Main export function ─────────────────────────────────────────────────────

export async function exportBudgetVarianceToExcel(projects: Project[]): Promise<void> {
  const enriched: EnrichedRow[] = projects.map((project) => {
    const { overallStatus, revenueVariance } = deriveOverallStatus(project);
    return { project, overallStatus, revenueVariance };
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Projects';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Budget Variance', {
    views: [{ state: 'frozen', ySplit: 2 }],
  });

  // ── Title row ─────────────────────────────────────────────────────
  const titleRow = sheet.addRow(['Budget Variance Report']);
  titleRow.height = 28;
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: HEADER_TEXT } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(1, 1, 1, COLUMNS.length);

  // ── Column header row ──────────────────────────────────────────────
  sheet.columns = COLUMNS.map((col) => ({
    key: col.key,
    width: col.width,
  }));

  const headerRow = sheet.addRow(COLUMNS.map((c) => c.header));
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
    };
  });

  // ── Data rows ──────────────────────────────────────────────────────
  enriched.forEach((row, idx) => {
    const values = COLUMNS.map((col) => col.getValue(row));
    const dataRow = sheet.addRow(values);
    dataRow.height = 18;

    const isAlt = idx % 2 === 1;
    dataRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];

      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }

      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };

      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }

      // Colour-code budget status
      if (colDef?.key === 'budgetStatus') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        if (cell.value === 'Over Budget') {
          cell.font = { size: 10, bold: true, color: { argb: 'DC2626' } };
        } else if (cell.value === 'At Risk') {
          cell.font = { size: 10, bold: true, color: { argb: 'D97706' } };
        } else if (cell.value === 'On Track') {
          cell.font = { size: 10, bold: true, color: { argb: '16A34A' } };
        }
      }

      // Colour-code variance columns (negative = good for cost/hours, positive = good for revenue)
      if (colDef?.key === 'revenueVariance' && typeof cell.value === 'number') {
        if (cell.value < 0) {
          cell.font = { size: 10, color: { argb: 'DC2626' } };
        } else if (cell.value > 0) {
          cell.font = { size: 10, color: { argb: '16A34A' } };
        }
      }
      if ((colDef?.key === 'costVariance' || colDef?.key === 'hoursVariance') && typeof cell.value === 'number') {
        if (cell.value > 0) {
          cell.font = { size: 10, color: { argb: 'DC2626' } };
        } else if (cell.value < 0) {
          cell.font = { size: 10, color: { argb: '16A34A' } };
        }
      }
    });
  });

  // ── Totals row ─────────────────────────────────────────────────────
  if (enriched.length > 0) {
    const totals = COLUMNS.map((col) => {
      if (NUMERIC_KEYS.has(col.key)) {
        return enriched.reduce((sum, r) => {
          const v = col.getValue(r);
          return sum + (typeof v === 'number' ? v : 0);
        }, 0);
      }
      if (col.key === 'projectName') return `Total (${enriched.length} projects)`;
      return null;
    });

    const totalsRow = sheet.addRow(totals);
    totalsRow.height = 20;
    totalsRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      cell.font = { bold: true, size: 10 };
      cell.border = {
        top: { style: 'thin', color: { argb: '9FB4D8' } },
      };
      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }

  // ── Auto-filter on header row ──────────────────────────────────────
  sheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: COLUMNS.length },
  };

  // ── Download ───────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const fileName = `Budget_Variance_Report_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
